import { localization } from '@ocean/configs'
import { DictionariesUtils } from '@ocean/utils'
import {
  GROUP_CARD_HEIGHT,
  PREVIEW_GROUP_CARD_HEIGHT,
  CATEGORY_CARD_HEIGHT,
  ACTIVITY_CARD_HEIGHT,
  PROFICIENCY_CARD_HEIGHT,
  EVENTS_SCHEDULE_CARD_HEIGHT,
  EVENT_CARD_HEIGHT,
} from 'config/cardConfig'
import {
  Card,
  CardType,
  ApiCardsDataResponseSpecificValues,
  ApiCardsDataResponseRequirementsGroupSpecificValues,
} from 'types/card'
import { DictionaryItem } from 'core/dictionaries/types'
import { UICardType } from 'components/cards/common/types'
import { namespace as aproDetailsNamespace } from 'modules/activityDetails/AproDetails/localization'
import { namespace as certificateDetailsNamespace } from 'modules/activityDetails/CertificateDetails/localization'
import { namespace as cesCstDetailsNamespace } from 'modules/activityDetails/CesCstDetails/localization'
import { namespace as cesDetailedDetailsNamespace } from 'modules/activityDetails/CesDetailedDetails/localization'
import { namespace as cesStcwDetailsNamespace } from 'modules/activityDetails/CesStcwDetails/localization'
import { namespace as companyNoticeDetailsNamespace } from 'modules/activityDetails/CompanyNoticeDetails/localization'
import { namespace as companySpecificTrainingDetailsNamespace } from 'modules/activityDetails/CompanySpecificTrainingDetails/localization'
import { namespace as courseDetailsNamespace } from 'modules/activityDetails/CourseDetails/localization'
import { namespace as distanceCourseDetailsNamespace } from 'modules/activityDetails/DistanceCourseDetails/localization'
import { namespace as educationDetailsNamespace } from 'modules/activityDetails/EducationDetails/localization'
import { namespace as elearningDetailsNamespace } from 'modules/activityDetails/ElearningDetails/localization'
import { namespace as onboardAssessmentDetailsNamespace } from 'modules/activityDetails/OnboardAssessmentDetails/localization'
import { namespace as pproDetailsNamespace } from 'modules/activityDetails/PproDetails/localization'
import { namespace as programDetailsNamespace } from 'modules/activityDetails/ProgramDetails/localization'
import { namespace as quickGuideDetailsNamespace } from 'modules/activityDetails/QuickGuideDetails/localization'
import { namespace as rapidElearningDetailsNamespace } from 'modules/activityDetails/RapidElearningDetails/localization'
import { namespace as selfAssessmentDetailsNamespace } from 'modules/activityDetails/SelfAssessmentDetails/localization'
import { namespace as serviceRecordDetailsNamespace } from 'modules/activityDetails/ServiceRecordDetails/localization'
import { namespace as surveyDetailsNamespace } from 'modules/activityDetails/SurveyDetails/localization'
import { namespace as videoDetailsNamespace } from 'modules/activityDetails/VideoDetails/localization'
import { namespace as requirementsNamespace } from 'modules/requirements/common/localization'

const { i18n } = localization

type ArrayValue = string | number

class CardUtils {
  getHeight(variant?: UICardType) {
    switch (variant) {
      case UICardType.Group:
        return `${GROUP_CARD_HEIGHT}px`
      case UICardType.PreviewGroup:
        return `${PREVIEW_GROUP_CARD_HEIGHT}px`
      case UICardType.Category:
      case UICardType.EventsCategory:
        return `${CATEGORY_CARD_HEIGHT}px`
      case UICardType.Activity:
        return `${ACTIVITY_CARD_HEIGHT}px`
      case UICardType.Proficiency:
        return `${PROFICIENCY_CARD_HEIGHT}px`
      case UICardType.EventsSchedule:
        return `${EVENTS_SCHEDULE_CARD_HEIGHT}px`
      case UICardType.Event:
        return `${EVENT_CARD_HEIGHT}px`
      default:
        return null
    }
  }

  checkRequirementsGroupCardType(card: Card) {
    return card.itemType === CardType.RequirementsGroup
  }

  checkElearningCategoryCardType(card: Card) {
    return card.itemType === CardType.ElearningCategory
  }

  getCardTypeName(cardType: CardType) {
    switch (cardType) {
      case CardType.UserRequirement:
        return i18n.t(`${requirementsNamespace}:requirementCompletionDetails.title`)
      case CardType.TrainingModule:
        return i18n.t(`${elearningDetailsNamespace}:elearningDetails.title`)
      case CardType.TrainingVideo:
        return i18n.t(`${videoDetailsNamespace}:videoDetails.title`)
      case CardType.PocketGuide:
        return i18n.t(`${quickGuideDetailsNamespace}:quickGuideDetails.title`)
      case CardType.DistanceCourse:
        return i18n.t(`${distanceCourseDetailsNamespace}:distanceCourseDetails.title`)
      case CardType.Rapid:
        return i18n.t(`${rapidElearningDetailsNamespace}:rapidElearningDetails.title`)
      case CardType.Survey:
        return i18n.t(`${surveyDetailsNamespace}:surveyDetails.title`)
      case CardType.Certificate:
        return i18n.t(`${certificateDetailsNamespace}:certificateDetails.title`)
      case CardType.CompanyNotice:
        return i18n.t(`${companyNoticeDetailsNamespace}:companyNoticeDetails.title`)
      case CardType.Education:
        return i18n.t(`${educationDetailsNamespace}:educationDetails.title`)
      case CardType.ServiceRecord:
        return i18n.t(`${serviceRecordDetailsNamespace}:serviceRecordDetails.title`)
      case CardType.Ppro:
        return i18n.t(`${pproDetailsNamespace}:pproDetails.title`)
      case CardType.CesDetailed:
        return i18n.t(`${cesDetailedDetailsNamespace}:cesDetailedDetails.title`)
      case CardType.Cst:
        return i18n.t(
          `${companySpecificTrainingDetailsNamespace}:companySpecificTrainingDetails.title`
        )
      case CardType.Course:
        return i18n.t(`${courseDetailsNamespace}:courseDetails.title`)
      case CardType.OnboardAssessment:
        return i18n.t(`${onboardAssessmentDetailsNamespace}:onboardAssessmentDetails.title`)
      case CardType.SelfAssessment:
        return i18n.t(`${selfAssessmentDetailsNamespace}:selfAssessmentDetails.title`)
      case CardType.CesStcw:
        return i18n.t(`${cesStcwDetailsNamespace}:cesStcwDetails.title`)
      case CardType.CesCompanySpecific:
        return i18n.t(`${cesCstDetailsNamespace}:cesCstDetails.title`)
      case CardType.Apro:
        return i18n.t(`${aproDetailsNamespace}:aproDetails.title`)
      case CardType.Program:
        return i18n.t(`${programDetailsNamespace}:programDetails.title`)
      default:
        return ''
    }
  }

  getUniqueCardTypeNames(cards: Card[]): string[] {
    const uniqueSet = new Set(cards.map(card => this.getCardTypeName(card.itemType)))
    uniqueSet.delete('')
    return Array.from(uniqueSet)
  }

  getUniqueValuesFromArrayProp<T extends ApiCardsDataResponseSpecificValues>(
    array: Card[],
    arrayPropName: keyof T
  ): ArrayValue[] {
    return Array.from(
      array.reduce((result, { typeSpecificValues }) => {
        const { [arrayPropName]: arrayPropValues = [] } = typeSpecificValues as T
        ;(arrayPropValues as ArrayValue[]).forEach(value => result.add(value))
        return result
      }, new Set<ArrayValue>())
    )
  }

  getDictionaryFromArrayProp<T extends ApiCardsDataResponseSpecificValues>(
    array: Card[],
    arrayPropName: keyof T,
    belongsToAllValue?: string
  ): DictionaryItem[] {
    let uniqueValues = this.getUniqueValuesFromArrayProp<T>(array, arrayPropName)
    if (belongsToAllValue) uniqueValues = uniqueValues.filter(value => value !== belongsToAllValue)
    return DictionariesUtils.createDictionaryFromArray(uniqueValues)
  }

  checkProficiencyCardsHasImages(cards: Card[]) {
    return cards.some(card => {
      const { hasPhoto } = (card.typeSpecificValues ||
        {}) as ApiCardsDataResponseRequirementsGroupSpecificValues
      return hasPhoto === true
    })
  }
}

export default new CardUtils()
